<template>
    <!--
        แฟลชเซล — create / edit a campaign as its own page.
        Spec: docs/specs/2026-10-05-flash-sale-campaign-page-design.md §3–§5.

        Two rules this page lives by:
          * the header is saved on its own (POST on /new, PATCH on /:id) and never
            carries targets — PUT /campaigns/:id replaces every target with what
            it is sent, and the products table below only ever holds ONE page;
          * products are added (modal → POST …/products) and removed (✕ → DELETE
            …/products/:product_id) one endpoint call at a time, effective at once.
        Rules worth testing live in @/utils/flashCampaign (jest cannot mount .vue).
    -->
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ isNew ? $t('app_catalog.flash_sale_new_title') : $t('app_catalog.flash_sale_edit_title', { id: $route.params.id }) }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <!-- Form draft spec 2026-10-07 §3 F2 / §4 A9: create mode
                         only (a non-admin never reaches /new), leftmost. -->
                    <form-clear-button v-if="isNew && isAdmin" :disabled="saving" @click="clearForm"></form-clear-button>
                    <base-button size="md" type="neutral" @click="goList">
                        {{ isAdmin ? $t("cancel") : $t("back") }}
                    </base-button>
                    <base-button
                        v-if="isAdmin && !notFound"
                        size="md"
                        type="primary"
                        :loading="saving"
                        data-test="flash-save"
                        @click="saveHeader"
                    >
                        <i class="fa fa-save"></i> {{ $t("save") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- E6: an id that does not exist -->
            <card v-if="notFound" class="text-center py-5">
                <p class="text-muted mb-3">{{ $t("app_catalog.flash_sale_not_found") }}</p>
                <base-button type="primary" size="sm" @click="goList">
                    {{ $t("app_catalog.flash_sale_back_to_list") }}
                </base-button>
            </card>

            <template v-else>
                <!-- ───────────── card 1: campaign header ───────────── -->
                <card v-loading="headerLoading">
                    <template slot="header">
                        <div class="d-flex align-items-center justify-content-between">
                            <h3 class="mb-0">{{ $t("app_catalog.flash_sale_info") }}</h3>
                            <el-tag v-if="!isNew" :type="phaseTag" size="medium" data-test="flash-phase">
                                {{ $t("app_catalog.phase_" + phase) }}
                            </el-tag>
                        </div>
                    </template>

                    <el-form label-width="160px" :disabled="!isAdmin" @submit.native.prevent>
                        <el-form-item :label="$t('app_catalog.flash_sale_name')" required>
                            <el-input v-model="form.name" maxlength="255" style="max-width: 520px"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.flash_sale_period')" required>
                            <el-date-picker
                                v-model="form.dateRange"
                                type="datetimerange"
                                range-separator="–"
                                :start-placeholder="$t('app_catalog.col_starts_at')"
                                :end-placeholder="$t('app_catalog.col_ends_at')"
                                value-format="yyyy-MM-dd HH:mm:ss"
                                :default-time="['00:00:00', '23:59:59']"
                                style="width: 420px; max-width: 100%"
                            ></el-date-picker>
                            <small class="text-muted ml-2">{{ $t("app_catalog.flash_sale_period_hint") }}</small>
                        </el-form-item>
                        <el-form-item :label="$t('app_catalog.flash_sale_discount_type')" required>
                            <el-radio-group v-model="form.kind">
                                <el-radio label="percent_off">{{ $t("app_catalog.campaign_kind_percent") }}</el-radio>
                                <el-radio label="fixed_off">{{ $t("app_catalog.campaign_kind_fixed") }}</el-radio>
                            </el-radio-group>
                            <span class="ml-4">
                                <el-input-number
                                    v-model="form.value"
                                    :min="0.01"
                                    :max="form.kind === 'percent_off' ? 100 : undefined"
                                    :precision="2"
                                    controls-position="right"
                                ></el-input-number>
                                <span class="ml-1">{{ form.kind === "percent_off" ? "%" : "฿" }}</span>
                            </span>
                        </el-form-item>
                        <el-form-item :label="$t('status')">
                            <el-radio-group v-model="form.status">
                                <el-radio label="draft">{{ $t("app_catalog.status_draft") }}</el-radio>
                                <el-radio label="active" :disabled="activeDisabled">
                                    {{ $t("app_catalog.status_active") }}
                                </el-radio>
                                <el-radio label="ended" :disabled="isNew">{{ $t("app_catalog.status_ended") }}</el-radio>
                            </el-radio-group>
                            <small v-if="activeDisabled && isAdmin" class="text-muted ml-2">
                                {{ $t("app_catalog.flash_sale_need_products") }}
                            </small>
                        </el-form-item>
                    </el-form>
                </card>

                <!-- ───────────── card 2: products in the campaign ───────────── -->
                <card body-classes="px-0 pb-1">
                    <template slot="header">
                        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px">
                            <h3 class="mb-0">{{ $t("app_catalog.flash_sale_products", { n: productTotal }) }}</h3>
                            <base-button
                                v-if="isAdmin && !isNew"
                                size="sm"
                                type="primary"
                                data-test="flash-add-products"
                                @click="openPicker"
                            >
                                <i class="fa fa-plus"></i> {{ $t("app_catalog.flash_sale_add_products") }}
                            </base-button>
                        </div>
                    </template>

                    <div v-if="isNew" class="text-center text-muted py-5" data-test="flash-save-first">
                        {{ $t("app_catalog.flash_sale_save_first") }}
                    </div>

                    <template v-else>
                        <div class="px-4 pb-3 d-flex" style="gap: 8px">
                            <el-input
                                v-model="keyword"
                                size="small"
                                clearable
                                style="max-width: 320px"
                                :placeholder="$t('app_catalog.flash_sale_search_placeholder')"
                                @keyup.enter.native="searchProducts"
                                @clear="searchProducts"
                            ></el-input>
                            <base-button size="sm" type="info" @click="searchProducts">
                                <i class="fa fa-search"></i> {{ $t("search") }}
                            </base-button>
                        </div>

                        <div
                            v-if="!productsLoading && productTotal === 0 && !keywordApplied"
                            class="text-center text-muted py-5"
                        >
                            <p>{{ $t("app_catalog.flash_sale_products_empty") }}</p>
                            <base-button v-if="isAdmin" size="sm" type="primary" @click="openPicker">
                                <i class="fa fa-plus"></i> {{ $t("app_catalog.flash_sale_add_products") }}
                            </base-button>
                        </div>

                        <el-table
                            v-else
                            ref="productTable"
                            v-loading="productsLoading"
                            :data="products"
                            row-key="product_id"
                            header-row-class-name="thead-light"
                            class="flash-sale-products"
                            :empty-text="$t('app_catalog.no_data')"
                            @expand-change="onExpandChange"
                        >
                            <!--
                                The SKU sub-table opens from the IMAGE only (owner:
                                "เมื่อคลิ๊กที่รูปให้ dropdown sku ออกมา"). The expand
                                column's own chevron is hidden by CSS; the image calls
                                toggleRowExpansion(row) and a click anywhere else on the
                                row does nothing.
                            -->
                            <el-table-column
                                type="expand"
                                width="1"
                                class-name="fs-expand-col"
                                label-class-name="fs-expand-col"
                            >
                                <template slot-scope="{ row }">
                                    <div class="px-4 py-2" v-loading="!!skuLoading[row.product_id]">
                                        <div v-if="skuError[row.product_id]" class="text-danger small">
                                            {{ $t("app_catalog.expand_load_failed") }}
                                        </div>
                                        <div
                                            v-else-if="!skuLoading[row.product_id] && skuRowsOf(row).length === 0"
                                            class="text-muted small"
                                        >
                                            {{ $t("app_catalog.expand_skus_empty") }}
                                        </div>
                                        <!-- Headerless: a continuation of the row above
                                             (same pattern as Products.vue). -->
                                        <el-table
                                            v-else
                                            :data="skuRowsOf(row)"
                                            :show-header="false"
                                            size="mini"
                                            row-key="key"
                                        >
                                            <el-table-column width="60">
                                                <template slot-scope="sku">
                                                    <img
                                                        v-if="thumbUrl(sku.row.image)"
                                                        :src="thumbUrl(sku.row.image)"
                                                        alt=""
                                                        class="fs-sku-thumb"
                                                    />
                                                    <div v-else class="fs-sku-thumb fs-thumb-empty">
                                                        <i class="fa fa-image"></i>
                                                    </div>
                                                </template>
                                            </el-table-column>
                                            <el-table-column min-width="180px">
                                                <template slot-scope="sku">
                                                    <div>{{ sku.row.sku_name || "-" }}</div>
                                                    <small class="text-muted">{{ sku.row.sku_code || "-" }}</small>
                                                </template>
                                            </el-table-column>
                                            <el-table-column min-width="110px" align="right">
                                                <template slot-scope="sku">{{ money(sku.row.price) }}</template>
                                            </el-table-column>
                                            <el-table-column min-width="110px" align="right">
                                                <template slot-scope="sku">
                                                    <span class="text-danger font-weight-bold">
                                                        {{ money(sku.row.campaign_price) }}
                                                    </span>
                                                </template>
                                            </el-table-column>
                                            <el-table-column min-width="90px" align="right">
                                                <template slot-scope="sku">
                                                    <el-tag v-if="Number(sku.row.quantity) === 0" size="mini" type="danger">
                                                        {{ $t("app_catalog.flash_sale_sold_out") }}
                                                    </el-tag>
                                                    <span v-else>
                                                        {{ $t("app_catalog.flash_sale_col_remaining") }} {{ sku.row.quantity }}
                                                    </span>
                                                </template>
                                            </el-table-column>
                                            <el-table-column min-width="220px">
                                                <template slot-scope="sku">
                                                    <el-tag v-if="!sku.row.is_active" size="mini" type="info" class="mr-1">
                                                        {{ $t("app_catalog.flash_sale_sku_closed") }}
                                                    </el-tag>
                                                    <el-tag v-if="skuFlashNote(sku.row)" size="mini" type="warning">
                                                        {{ $t(skuFlashNote(sku.row).key, { price: skuFlashNote(sku.row).price }) }}
                                                    </el-tag>
                                                </template>
                                            </el-table-column>
                                        </el-table>
                                    </div>
                                </template>
                            </el-table-column>

                            <el-table-column :label="$t('app_catalog.col_image')" width="80px" align="center">
                                <template slot-scope="{ row }">
                                    <el-tooltip :content="productDisplayName(row)" placement="top" :open-delay="200">
                                        <div
                                            class="fs-thumb-wrap"
                                            data-test="flash-image-toggle"
                                            :title="$t('app_catalog.flash_sale_image_hint')"
                                            @click.stop="toggleSkus(row)"
                                        >
                                            <img
                                                v-if="thumbUrl(row.image_url)"
                                                :src="thumbUrl(row.image_url)"
                                                alt=""
                                                class="fs-thumb"
                                            />
                                            <div v-else class="fs-thumb fs-thumb-empty"><i class="fa fa-image"></i></div>
                                            <i
                                                class="fs-caret fa"
                                                :class="expandedIds[row.product_id] ? 'fa-caret-down' : 'fa-caret-right'"
                                            ></i>
                                        </div>
                                    </el-tooltip>
                                </template>
                            </el-table-column>
                            <!-- Owner 2026-10-05 follow-up: the NAME opens the SKU
                                 sub-table (the SKU-count column is gone — the SKUs
                                 themselves are one click away). The image still does too. -->
                            <el-table-column :label="$t('name')" min-width="260px" show-overflow-tooltip>
                                <template slot-scope="{ row }">
                                    <a
                                        href="#"
                                        class="fs-name-toggle text-truncate d-block"
                                        data-test="flash-name-toggle"
                                        :title="$t('app_catalog.flash_sale_name_hint')"
                                        @click.prevent.stop="toggleSkus(row)"
                                    >
                                        <i
                                            class="fa mr-1"
                                            :class="expandedIds[row.product_id] ? 'fa-caret-down' : 'fa-caret-right'"
                                        ></i>{{ productDisplayName(row) }}
                                    </a>
                                    <small class="text-muted">{{ row.code }}</small>
                                </template>
                            </el-table-column>
                            <el-table-column
                                :label="$t('app_catalog.flash_sale_col_normal_price')"
                                min-width="130px"
                                align="right"
                            >
                                <template slot-scope="{ row }">{{ priceRangeText(row.price_min, row.price_max) }}</template>
                            </el-table-column>
                            <el-table-column
                                :label="$t('app_catalog.flash_sale_col_sale_price')"
                                min-width="130px"
                                align="right"
                            >
                                <template slot-scope="{ row }">
                                    <span class="text-danger font-weight-bold">
                                        {{ priceRangeText(row.campaign_price_min, row.campaign_price_max) }}
                                    </span>
                                </template>
                            </el-table-column>
                            <!-- No คงเหลือ on the product row (owner 2026-10-05): the
                                 quantity is per SKU and shows in the SKU expand only. -->
                            <el-table-column min-width="180px">
                                <template slot-scope="{ row }">
                                    <el-tag
                                        v-if="Number(row.flash_conflict_skus) > 0"
                                        size="mini"
                                        type="warning"
                                        class="mr-1 mb-1"
                                    >
                                        {{ $t("app_catalog.flash_sale_flash_conflict", { n: row.flash_conflict_skus }) }}
                                    </el-tag>
                                    <el-tooltip
                                        v-for="o in row.overlaps || []"
                                        :key="'ov-' + o.id"
                                        :content="$t('app_catalog.flash_sale_overlap_hint')"
                                        placement="top"
                                    >
                                        <el-tag size="mini" type="info" class="mr-1 mb-1">
                                            {{ $t("app_catalog.flash_sale_overlap", { name: o.name, id: o.id }) }}
                                        </el-tag>
                                    </el-tooltip>
                                    <el-tag v-if="Number(row.app_enable) === 0" size="mini" type="danger" class="mb-1">
                                        {{ $t("app_catalog.flash_sale_app_disabled") }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <!-- ⋮ kebab, same pattern as Products.vue / Categories.vue -->
                            <el-table-column :label="$t('actions')" width="72px" align="center">
                                <template slot-scope="{ row }">
                                    <el-dropdown
                                        trigger="click"
                                        data-test="flash-product-kebab"
                                        @command="cmd => onProductCommand(cmd, row)"
                                    >
                                        <base-button
                                            size="sm"
                                            type="secondary"
                                            class="btn-icon-only"
                                            :title="$t('actions')"
                                            @click.native.stop
                                        >
                                            <i class="fa fa-ellipsis-v"></i>
                                        </base-button>
                                        <el-dropdown-menu slot="dropdown">
                                            <el-dropdown-item command="skus">
                                                <i class="fa fa-list text-info mr-2"></i>{{ $t("app_catalog.flash_sale_view_skus") }}
                                            </el-dropdown-item>
                                            <el-dropdown-item v-if="isAdmin" command="edit_product">
                                                <i class="fa fa-edit text-info mr-2"></i>{{ $t("app_catalog.edit_product") }}
                                            </el-dropdown-item>
                                            <el-dropdown-item v-if="isAdmin" command="remove" divided>
                                                <span class="text-danger" data-test="flash-remove-product">
                                                    <i class="fa fa-times mr-2"></i>{{ $t("app_catalog.flash_sale_remove") }}
                                                </span>
                                            </el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                </template>
                            </el-table-column>
                        </el-table>

                        <div class="d-flex justify-content-end px-4 py-3">
                            <el-pagination
                                background
                                layout="total, sizes, prev, pager, next"
                                :page-sizes="[10, 20, 50]"
                                :page-size="pageSize"
                                :current-page="page"
                                :total="productTotal"
                                @size-change="onPageSizeChange"
                                @current-change="onPageChange"
                            ></el-pagination>
                        </div>
                    </template>
                </card>
            </template>
        </div>

        <!-- ───────────── add-product modal (spec §4) ─────────────
             The WMS-import modal's pattern (WmsProductList.vue) plus what that
             one lacks: row-key + reserve-selection, so ticks survive paging. -->
        <el-dialog
            :title="$t('app_catalog.flash_sale_add_products')"
            :visible.sync="pickerVisible"
            width="1100px"
            append-to-body
            custom-class="flash-sale-picker"
            @open="onPickerOpen"
        >
            <div class="mb-3 d-flex" style="gap: 8px">
                <el-input
                    v-model="pickerKeyword"
                    size="small"
                    clearable
                    style="max-width: 320px"
                    :placeholder="$t('app_catalog.flash_sale_search_placeholder')"
                    @keyup.enter.native="searchPicker"
                    @clear="searchPicker"
                ></el-input>
                <base-button size="sm" type="info" :loading="pickerLoading" @click="searchPicker">
                    <i class="fa fa-search"></i> {{ $t("search") }}
                </base-button>
            </div>

            <el-table
                ref="pickerTable"
                v-loading="pickerLoading"
                :data="pickerRows"
                row-key="id"
                size="small"
                max-height="560"
                style="width: 100%"
                class="flash-picker-table"
                @selection-change="onPickerSelectionChange"
                @expand-change="onPickerExpandChange"
            >
                <!-- Owner 2026-10-05 follow-up: clicking a product NAME shows its
                     SKUs (code, image, price, sellable qty) under the row. The
                     expand column's own chevron is hidden; the name toggles it.
                     SKUs come from the product detail (GET /products/:id), lazily,
                     cached per product — the campaign SKU endpoint (BE-4) answers
                     only for products already in the campaign. -->
                <el-table-column
                    type="expand"
                    width="1"
                    class-name="fs-expand-col"
                    label-class-name="fs-expand-col"
                >
                    <template slot-scope="{ row }">
                        <div class="px-4 py-2" v-loading="!!pickerSkuLoading[row.id]">
                            <div v-if="pickerSkuError[row.id]" class="text-danger small">
                                {{ $t("app_catalog.expand_load_failed") }}
                            </div>
                            <div
                                v-else-if="!pickerSkuLoading[row.id] && pickerSkuRowsOf(row).length === 0"
                                class="text-muted small"
                            >
                                {{ $t("app_catalog.expand_skus_empty") }}
                            </div>
                            <el-table
                                v-else
                                :data="pickerSkuRowsOf(row)"
                                :show-header="false"
                                size="mini"
                                row-key="key"
                                data-test="flash-picker-skus"
                            >
                                <el-table-column width="60">
                                    <template slot-scope="sku">
                                        <img
                                            v-if="thumbUrl(sku.row.image)"
                                            :src="thumbUrl(sku.row.image)"
                                            alt=""
                                            class="fs-sku-thumb"
                                        />
                                        <div v-else class="fs-sku-thumb fs-thumb-empty"><i class="fa fa-image"></i></div>
                                    </template>
                                </el-table-column>
                                <el-table-column min-width="200px">
                                    <template slot-scope="sku">
                                        <div>{{ sku.row.sku_name || "-" }}</div>
                                        <small class="text-muted">{{ sku.row.sku_code || "-" }}</small>
                                    </template>
                                </el-table-column>
                                <el-table-column min-width="120px" align="right">
                                    <template slot-scope="sku">{{ money(sku.row.price) }}</template>
                                </el-table-column>
                                <el-table-column min-width="100px" align="right">
                                    <template slot-scope="sku">
                                        <el-tag v-if="Number(sku.row.quantity) === 0" size="mini" type="danger">
                                            {{ $t("app_catalog.flash_sale_sold_out") }}
                                        </el-tag>
                                        <span v-else>
                                            {{ $t("app_catalog.flash_sale_col_remaining") }} {{ sku.row.quantity }}
                                        </span>
                                    </template>
                                </el-table-column>
                                <el-table-column min-width="90px">
                                    <template slot-scope="sku">
                                        <el-tag v-if="!sku.row.is_active" size="mini" type="info">
                                            {{ $t("app_catalog.flash_sale_sku_closed") }}
                                        </el-tag>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    type="selection"
                    width="50px"
                    :reserve-selection="true"
                    :selectable="pickerRowSelectable"
                ></el-table-column>
                <el-table-column :label="$t('app_catalog.col_image')" width="70px" align="center">
                    <template slot-scope="{ row }">
                        <el-tooltip :content="productDisplayName(row)" placement="top" :open-delay="200">
                            <img v-if="thumbUrl(row.image_url)" :src="thumbUrl(row.image_url)" alt="" class="fs-thumb" />
                            <div v-else class="fs-thumb fs-thumb-empty"><i class="fa fa-image"></i></div>
                        </el-tooltip>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('name')" min-width="320px" show-overflow-tooltip>
                    <template slot-scope="{ row }">
                        <a
                            href="#"
                            class="fs-name-toggle"
                            data-test="flash-picker-name-toggle"
                            :title="$t('app_catalog.flash_sale_name_hint')"
                            @click.prevent.stop="togglePickerSkus(row)"
                        >
                            <i
                                class="fa mr-1"
                                :class="pickerExpandedIds[row.id] ? 'fa-caret-down' : 'fa-caret-right'"
                            ></i>{{ productDisplayName(row) }}
                        </a>
                        <el-tag v-if="!pickerRowSelectable(row)" size="mini" type="info" class="ml-2">
                            {{ $t("app_catalog.flash_sale_in_campaign") }}
                        </el-tag>
                        <el-tag v-if="Number(row.app_enable) === 0" size="mini" type="danger" class="ml-1">
                            {{ $t("app_catalog.flash_sale_app_disabled") }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('app_catalog.flash_sale_col_normal_price')" min-width="140px" align="right">
                    <template slot-scope="{ row }">{{ priceRangeText(row.price_min, row.price_max) }}</template>
                </el-table-column>
                <!-- No คงเหลือ on the product row (owner 2026-10-05): per-SKU
                     quantity shows in the name-click SKU expand only. -->
            </el-table>

            <div class="mt-3 d-flex justify-content-end">
                <el-pagination
                    background
                    layout="total, sizes, prev, pager, next"
                    :page-sizes="[10, 20, 50]"
                    :page-size="pickerPageSize"
                    :current-page="pickerPage"
                    :total="pickerTotal"
                    @size-change="onPickerSizeChange"
                    @current-change="loadPickerPage"
                ></el-pagination>
            </div>

            <div slot="footer" class="d-flex justify-content-between align-items-center">
                <div>
                    <span data-test="flash-picker-selected">
                        {{ $t("app_catalog.flash_sale_selected", { n: pickerSelectedCount }) }}
                    </span>
                    <a v-if="pickerSelectedCount > 0" href="#" class="ml-2" @click.prevent="clearPickerSelection">
                        {{ $t("app_catalog.flash_sale_clear_selection") }}
                    </a>
                </div>
                <div>
                    <base-button type="secondary" @click="pickerVisible = false">{{ $t("cancel") }}</base-button>
                    <base-button
                        type="primary"
                        :loading="adding"
                        :disabled="!pickerCanConfirm"
                        @click="confirmAdd"
                    >
                        {{ $t("app_catalog.flash_sale_add_products") }} ({{ pickerSelectedCount }})
                    </base-button>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { requestRefresh } from "@/utils/pageRefresh";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";
import swal from "sweetalert2";
import {
    Table,
    TableColumn,
    Tag,
    Tooltip,
    Pagination,
    Dialog,
    Form,
    FormItem,
    Input,
    InputNumber,
    RadioGroup,
    Radio,
    DatePicker,
    Dropdown,
    DropdownMenu,
    DropdownItem
} from "element-ui";
import { skuRowsOf } from "@/utils/sellerProductRow";
import { cdnImageUrl } from "@/utils/cdnImage";
import {
    FLASH_DEFAULT_PAGE_SIZE,
    campaignPhase,
    phaseTagType,
    validateHeader,
    headerPayload,
    formFromCampaign,
    addProductsPayload,
    selectionCount,
    canConfirmSelection,
    pickerRowSelectable,
    pageParams,
    pickerParams,
    productDisplayName,
    money,
    priceRangeText,
    skuFlashNote,
    campaignSkuRows,
    campaignProductsBody
} from "@/utils/flashCampaign";

const emptyForm = () => ({
    name: "",
    kind: "percent_off",
    value: 10,
    dateRange: null,
    status: "draft"
});

export default {
    name: "AppCatalogFlashSaleEdit",
    // Form draft (spec 2026-10-07 §3/§4 A9): /new keeps its draft while the tab
    // is cached, clears on "ล้างฟอร์ม" and right after a successful create.
    mixins: [formDraft],
    components: {
        FormClearButton,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Pagination.name]: Pagination,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [RadioGroup.name]: RadioGroup,
        [Radio.name]: Radio,
        [DatePicker.name]: DatePicker,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem
    },
    data() {
        return {
            form: emptyForm(),
            savedSnapshot: JSON.stringify(headerPayload(emptyForm())),
            campaign: null,
            headerLoading: false,
            saving: false,
            notFound: false,
            // products table (server-side paging, BE-3)
            products: [],
            productTotal: 0,
            productsLoading: false,
            page: 1,
            pageSize: FLASH_DEFAULT_PAGE_SIZE,
            keyword: "",
            keywordApplied: "",
            expandedIds: {},
            skuById: {},
            skuLoading: {},
            skuError: {},
            // add-product modal (BE-5 + BE-6)
            pickerVisible: false,
            pickerKeyword: "",
            pickerRows: [],
            pickerTotal: 0,
            pickerPage: 1,
            // The modal is the big one now (owner follow-up): a full page of 50.
            pickerPageSize: 50,
            pickerExpandedIds: {},
            pickerSkuById: {},
            pickerSkuLoading: {},
            pickerSkuError: {},
            pickerLoading: false,
            pickerSelection: [],
            adding: false
        };
    },
    computed: {
        ...mapGetters({ isAdmin: "Auth/isAdmin" }),
        isNew() {
            return this.$route.name === "AppCatalogFlashSaleNew";
        },
        campaignId() {
            return this.isNew ? null : Number(this.$route.params.id) || null;
        },
        phase() {
            // The SAVED campaign's phase: the badge says what customers see now,
            // not what an unsaved edit would make it.
            return campaignPhase(this.campaign || { status: "draft" });
        },
        phaseTag() {
            return phaseTagType(this.phase);
        },
        // E3: no activating an empty campaign. Left enabled when it is already
        // active (the last product may have been removed — allowed, spec Q3).
        activeDisabled() {
            if (this.isNew) return true;
            return this.productTotal === 0 && !(this.campaign && this.campaign.status === "active");
        },
        isDirty() {
            if (!this.isAdmin || this.notFound) return false;
            return JSON.stringify(headerPayload(this.form)) !== this.savedSnapshot;
        },
        pickerSelectedCount() {
            return selectionCount(this.pickerSelection);
        },
        pickerCanConfirm() {
            return canConfirmSelection(this.pickerSelection);
        }
    },
    watch: {
        // Only for OUR two routes: while this instance is being torn down on the
        // way to another page the watcher still sees the new route, and an
        // unrelated :id (e.g. /seller/bundles/7/edit) must not fetch campaign 7.
        //
        // Form draft (spec 2026-10-07 §4 A9): since the taskbar keep-alive
        // (2026-10-06) the router-view is keyed PER PATH (DashboardLayout
        // viewKey), so /new and every /:id are separate cached instances and
        // one instance never changes path. A cached instance still sees every
        // route change, though, so:
        //   * a route for ANOTHER path is ignored — otherwise the cached /new
        //     instance loads the campaign just created (/new → replace → /:id)
        //     right after afterSaveReset() emptied it, and an /:id tab is
        //     blanked every time /new is opened;
        //   * arriving back on /new keeps the draft (F4) — no re-init;
        //   * arriving back on an /:id tab re-reads the campaign, as before.
        $route(to, from) {
            const ours = ["AppCatalogFlashSaleNew", "AppCatalogFlashSaleEdit"];
            if (!to || ours.indexOf(to.name) < 0) return;
            if (from && to.fullPath === from.fullPath) return;
            if (this.boundPath && to.path !== this.boundPath) return;
            // /new keeps its draft (F4); a non-admin still gets init()'s redirect
            // on re-entry (verifier advisory 2026-10-07).
            if (this.isNew) {
                if (!this.isAdmin) this.init();
                return;
            }
            this.init();
        }
    },
    created() {
        // The path this cached instance belongs to (see the $route watcher).
        this.boundPath = this.$route.path;
    },
    mounted() {
        this.init();
    },
    beforeRouteLeave(to, from, next) {
        // A /new draft survives tab switches now (spec §3 F4), so the
        // "unsaved changes" prompt only guards /:id edits.
        if (!this.isDirty || this.isNew) return next();
        swal.fire({
            title: this.$t("app_catalog.flash_sale_unsaved_confirm"),
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: this.$t("app_catalog.flash_sale_leave"),
            cancelButtonText: this.$t("app_catalog.btn_cancel")
        }).then(result => next(!!result.value));
    },
    methods: {
        // ---- Form draft (spec 2026-10-07 §3 F1 / §4 A9) ----
        // Back to a blank NEW campaign: every field of data() — the header
        // form (name, kind, value, period, status), the saved snapshot (so the
        // page is clean and beforeRouteLeave does not ask), campaign/notFound,
        // the products table state and the add-product modal state. Only
        // offered / called on /new.
        resetForm() {
            Object.assign(this.$data, this.$options.data.call(this));
        },
        ...mapActions({
            getCampaign: "AppCatalogCampaign/getCampaign",
            createHeader: "AppCatalogCampaign/createHeader",
            patchHeader: "AppCatalogCampaign/patch",
            fetchCampaignProducts: "AppCatalogCampaign/fetchProducts",
            fetchProductSkus: "AppCatalogCampaign/fetchProductSkus",
            addCampaignProducts: "AppCatalogCampaign/addProducts",
            removeCampaignProduct: "AppCatalogCampaign/removeProduct",
            pickerProducts: "AppCatalogCampaign/pickerProducts",
            fetchProductDetail: "AppCatalogProduct/fetchDetail"
        }),
        // ---- template helpers (all logic in @/utils/flashCampaign) ----
        productDisplayName,
        money,
        priceRangeText,
        skuFlashNote,
        pickerRowSelectable,
        thumbUrl(url) {
            return cdnImageUrl(url);
        },
        pickerSkuRowsOf(row) {
            return this.pickerSkuById[row && row.id] || [];
        },
        skuRowsOf(row) {
            return this.skuById[row && row.product_id] || [];
        },
        goList() {
            this.$router.push({ name: "AppCatalogPromotions" });
        },

        // ---- load ----
        async init() {
            this.notFound = false;
            this.products = [];
            this.productTotal = 0;
            this.page = 1;
            this.keyword = "";
            this.keywordApplied = "";
            this.expandedIds = {};
            this.skuById = {};
            this.skuLoading = {};
            this.skuError = {};
            if (this.isNew) {
                // E5: a non-admin seller cannot create.
                if (!this.isAdmin) {
                    this.$router.replace({ name: "AppCatalogPromotions" });
                    return;
                }
                this.campaign = null;
                this.form = emptyForm();
                this.savedSnapshot = JSON.stringify(headerPayload(this.form));
                return;
            }
            if (!this.campaignId) {
                this.notFound = true;
                return;
            }
            await this.loadHeader();
            if (!this.notFound) await this.loadProducts();
        },
        async loadHeader() {
            this.headerLoading = true;
            try {
                const c = await this.getCampaign(this.campaignId);
                if (!c) {
                    this.notFound = true;
                    return;
                }
                this.applyCampaign(c);
            } catch (err) {
                if (err && err.response && err.response.status === 404) {
                    this.notFound = true;
                } else {
                    this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
                }
            } finally {
                this.headerLoading = false;
            }
        },
        applyCampaign(c) {
            this.campaign = c;
            this.form = formFromCampaign(c);
            this.savedSnapshot = JSON.stringify(headerPayload(this.form));
        },
        async loadProducts() {
            if (!this.campaignId) return;
            this.productsLoading = true;
            try {
                const body = await this.fetchCampaignProducts({
                    id: this.campaignId,
                    params: pageParams({ page: this.page, pageSize: this.pageSize, keyword: this.keywordApplied })
                });
                const parsed = campaignProductsBody(body);
                this.products = parsed.rows;
                this.productTotal = parsed.total;
                this.expandedIds = {};
            } catch (err) {
                if (err && err.response && err.response.status === 404) {
                    this.notFound = true;
                } else {
                    this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
                }
            } finally {
                this.productsLoading = false;
            }
        },
        searchProducts() {
            this.keywordApplied = (this.keyword || "").trim();
            this.page = 1;
            this.loadProducts();
        },
        onPageChange(p) {
            this.page = p;
            this.loadProducts();
        },
        onPageSizeChange(size) {
            this.pageSize = size;
            this.page = 1;
            this.loadProducts();
        },

        // ---- SKU sub-table: opened from the image only ----
        toggleSkus(row) {
            const table = this.$refs.productTable;
            if (table && typeof table.toggleRowExpansion === "function") {
                table.toggleRowExpansion(row);
            }
        },
        // element-ui sends (row, expandedRows) for a type="expand" column and
        // (row, expanded) in some builds — handle both (Products.vue does too).
        onExpandChange(row, expanded) {
            const isOpen = Array.isArray(expanded)
                ? expanded.some(r => r && r.product_id === row.product_id)
                : !!expanded;
            this.$set(this.expandedIds, row.product_id, isOpen);
            if (isOpen) this.loadSkus(row.product_id);
        },
        async loadSkus(productId) {
            // Once per product for the life of the page.
            if (!productId || this.skuById[productId] || this.skuLoading[productId]) return;
            this.$set(this.skuLoading, productId, true);
            this.$set(this.skuError, productId, false);
            try {
                const body = await this.fetchProductSkus({ id: this.campaignId, productId });
                this.$set(this.skuById, productId, campaignSkuRows(body));
            } catch (err) {
                this.$set(this.skuError, productId, true);
            } finally {
                this.$set(this.skuLoading, productId, false);
            }
        },

        // ---- header save: POST on /new, PATCH on /:id. Never PUT. ----
        async saveHeader() {
            if (!this.isAdmin || this.saving) return;
            const errors = validateHeader(this.form, this.isNew ? 0 : this.productTotal);
            if (errors.length) {
                this.$notify({ type: "danger", message: errors.map(k => this.$t(k)).join(" · ") });
                return;
            }
            const payload = headerPayload(this.form);
            this.saving = true;
            try {
                if (this.isNew) {
                    payload.status = "draft";
                    const body = await this.createHeader(payload);
                    const created = (body && body.data) || {};
                    // Form draft §4 A9: empty the cached /new tab BEFORE the
                    // replace below. The reset also marks the page clean, so
                    // beforeRouteLeave does not ask on the way out. The /:id
                    // page is a separate instance (keyed per path) that loads
                    // the new campaign on mount; this instance ignores that
                    // route (see the $route watcher), so nothing refills it.
                    this.afterSaveReset();
                    // The list gains a row: refresh it whenever the user next arrives there
                    // (the flag waits while they stay on /:id — spec §2).
                    requestRefresh("/seller/flash-sales");
                    this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                    this.warnOverlap(body);
                    if (created.id) {
                        this.$router.replace({ name: "AppCatalogFlashSaleEdit", params: { id: String(created.id) } });
                    } else {
                        this.goList();
                    }
                    return;
                }
                const body = await this.patchHeader({ id: this.campaignId, payload });
                const updated = (body && body.data) || null;
                if (updated) this.applyCampaign(Object.assign({}, this.campaign || {}, updated));
                else this.savedSnapshot = JSON.stringify(payload);
                requestRefresh("/seller/flash-sales"); // name/dates/status changed in the list
                this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                this.warnOverlap(body);
            } catch (err) {
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.saving = false;
            }
        },
        // E2: overlapping another flash sale is allowed — a warning, not a block.
        warnOverlap(body) {
            const n = Number(body && body.overlap_count) || 0;
            if (n > 0) {
                this.$notify({ type: "warning", message: this.$t("app_catalog.flash_sale_overlap_warn", { n }) });
            }
        },

        // ---- row kebab ----
        onProductCommand(cmd, row) {
            if (cmd === "skus") return this.toggleSkus(row);
            if (cmd === "edit_product") {
                return this.$router.push({ name: "AppCatalogProductEdit", params: { id: String(row.product_id) } });
            }
            if (cmd === "remove") return this.confirmRemove(row);
        },

        // ---- remove one product (BE-7) ----
        confirmRemove(row) {
            if (!this.isAdmin || !row) return;
            swal.fire({
                title: this.$t("app_catalog.flash_sale_remove_confirm"),
                text: productDisplayName(row),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async result => {
                if (!result.value) return;
                try {
                    await this.removeCampaignProduct({ id: this.campaignId, productId: row.product_id });
                    this.$delete(this.skuById, row.product_id);
                    // The last row of a page that is not the first: step back.
                    if (this.products.length === 1 && this.page > 1) this.page -= 1;
                    await this.loadProducts();
                    this.$notify({ type: "success", message: this.$t("app_catalog.delete_success") });
                } catch (err) {
                    this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
                }
            });
        },

        // ---- add-product modal ----
        openPicker() {
            if (!this.isAdmin || !this.campaignId) return;
            this.pickerVisible = true;
        },
        // Page 1 on open, no search needed first (wmsImportModal.js lesson).
        onPickerOpen() {
            this.loadPickerPage(1);
        },
        searchPicker() {
            return this.loadPickerPage(1);
        },
        onPickerSizeChange(size) {
            this.pickerPageSize = size;
            this.loadPickerPage(1);
        },
        async loadPickerPage(page) {
            this.pickerLoading = true;
            try {
                const params = pickerParams({
                    keyword: this.pickerKeyword,
                    page,
                    pageSize: this.pickerPageSize,
                    campaignId: this.campaignId
                });
                const body = await this.pickerProducts(params);
                this.pickerRows = Array.isArray(body.data) ? body.data : [];
                this.pickerExpandedIds = {};
                this.pickerTotal = Number(body.total) || this.pickerRows.length;
                this.pickerPage = params.page;
            } catch (err) {
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.pickerLoading = false;
            }
        },
        // ---- picker SKU rows: opened from the product NAME ----
        togglePickerSkus(row) {
            const table = this.$refs.pickerTable;
            if (table && typeof table.toggleRowExpansion === "function") {
                table.toggleRowExpansion(row);
            }
        },
        onPickerExpandChange(row, expanded) {
            const isOpen = Array.isArray(expanded)
                ? expanded.some(r => r && r.id === row.id)
                : !!expanded;
            this.$set(this.pickerExpandedIds, row.id, isOpen);
            if (isOpen) this.loadPickerSkus(row.id);
        },
        async loadPickerSkus(productId) {
            // Once per product for the life of the page (cached across pages/searches).
            if (!productId || this.pickerSkuById[productId] || this.pickerSkuLoading[productId]) return;
            this.$set(this.pickerSkuLoading, productId, true);
            this.$set(this.pickerSkuError, productId, false);
            try {
                const detail = await this.fetchProductDetail(productId);
                this.$set(this.pickerSkuById, productId, skuRowsOf(detail || {}));
            } catch (err) {
                this.$set(this.pickerSkuError, productId, true);
            } finally {
                this.$set(this.pickerSkuLoading, productId, false);
            }
        },
        // With reserve-selection the table reports the ticks of EVERY page.
        onPickerSelectionChange(selection) {
            this.pickerSelection = selection || [];
        },
        clearPickerSelection() {
            if (this.$refs.pickerTable) this.$refs.pickerTable.clearSelection();
            this.pickerSelection = [];
        },
        async confirmAdd() {
            if (!this.pickerCanConfirm || this.adding) return;
            const { product_ids } = addProductsPayload(this.pickerSelection);
            this.adding = true;
            try {
                const body = await this.addCampaignProducts({ id: this.campaignId, productIds: product_ids });
                this.$notify({
                    type: "success",
                    message: this.$t("app_catalog.flash_sale_add_result", {
                        added: Number(body.added) || 0,
                        skipped: Number(body.skipped) || 0
                    })
                });
                this.clearPickerSelection();
                this.pickerVisible = false;
                this.page = 1;
                await this.loadProducts();
            } catch (err) {
                // Keep the selection so nothing has to be ticked again.
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.adding = false;
            }
        }
    }
};
</script>

<style scoped>
.fs-thumb-wrap {
    position: relative;
    display: inline-block;
    cursor: pointer;
}
.fs-thumb {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
}
.fs-sku-thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
}
.fs-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e9ecef;
    color: #525f7f;
}
.fs-caret {
    position: absolute;
    right: -2px;
    bottom: -2px;
    font-size: 12px;
    background: #fff;
    border-radius: 50%;
    width: 14px;
    height: 14px;
    line-height: 14px;
    text-align: center;
    color: #525f7f;
}
.fs-name-toggle {
    cursor: pointer;
    color: #32325d;
    font-weight: 600;
}
.fs-name-toggle:hover {
    text-decoration: underline;
}
/* The expand column exists only to host the SKU sub-table: its chevron is
   hidden so the image is the one place that opens it. */
.flash-sale-products >>> .fs-expand-col .cell {
    display: none;
}
.flash-sale-products >>> .fs-expand-col {
    padding: 0;
}
</style>

<!-- NOT scoped: the modal is append-to-body. -->
<style>
.flash-sale-picker {
    max-width: 96vw;
}
/* The picker's expand column only hosts the SKU rows: the product name opens
   it, so its chevron is hidden (append-to-body ⇒ not in the scoped block). */
.flash-picker-table .fs-expand-col .cell {
    display: none;
}
.flash-picker-table .fs-expand-col {
    padding: 0;
}
</style>
